import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Tabs, Card, Stack, Group, Text, Table, Center, Loader, Alert, Box, Badge,
  Button, ActionIcon, Switch, Chip, ScrollArea, Modal, TextInput, NumberInput, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Plus, Pencil, Trash2, Check } from 'lucide-react';

import { gamesApi, walkieApi, type WalkieStation } from '../../api';
import { Leaderboard } from '../Leaderboard';
import { Eyebrow } from '../Eyebrow';
import { GroupTag } from '../GroupTag';
import { SituationMap, type MapStation } from '../SituationMap';
import { numStyle } from '../../theme';
import { formatPoints, plural } from '../../utils/format';

const INTERVAL_PRESETS = [5, 10, 12, 15, 20, 30];
const MAX_STATIONS = 50;

function stationLabel(s: { position: number; name: string | null }) {
  return s.name && s.name.trim() ? s.name : `Stanoviště ${s.position}`;
}

export function WalkieResults({ gameUuid, live }: { gameUuid: string; live: boolean }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<string | null>('board');

  const rounds = useQuery({
    queryKey: ['walkieRounds', gameUuid],
    queryFn: () => gamesApi.getWalkieRounds(gameUuid),
    refetchInterval: live ? 20_000 : false,
  });
  const state = useQuery({
    queryKey: ['walkieState', gameUuid],
    queryFn: () => walkieApi.getState(gameUuid),
    refetchInterval: live ? 20_000 : false,
  });

  if (rounds.isLoading || state.isLoading) return <Card><Center h={140}><Loader size="sm" /></Center></Card>;
  if (rounds.error || state.error) {
    const e: any = rounds.error || state.error;
    return <Alert color="red" title="Vysílačky se nepodařilo načíst">{e?.response?.data?.error || e?.message}</Alert>;
  }

  const data = rounds.data!;
  const stations = state.data?.stations ?? [];

  return (
    <Tabs value={tab} onChange={setTab}>
      <Tabs.List>
        <Tabs.Tab value="board">Žebříček</Tabs.Tab>
        <Tabs.Tab value="stations">Stanoviště</Tabs.Tab>
        <Tabs.Tab value="rounds">Historie kol</Tabs.Tab>
        <Tabs.Tab value="config">Nastavení</Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="board" pt="sm">
        {data.leaderboard.length === 0 ? (
          <Card><Text c="dimmed" ta="center" py="lg">Zatím se nikdo neozval.</Text></Card>
        ) : (
          <Card padding={0}>
            <Leaderboard
              rows={data.leaderboard.map((g) => ({
                key: g.group_id,
                name: g.group_name,
                sub: `${g.calls_won}× se ozvali · ${plural(data.rounds.length, 'kolo', 'kola', 'kol')}`,
                value: g.total_points,
                color: g.group_color,
              }))}
            />
          </Card>
        )}
      </Tabs.Panel>

      <Tabs.Panel value="stations" pt="sm">
        <StationsPanel gameUuid={gameUuid} stations={stations} rounds={data} />
      </Tabs.Panel>

      <Tabs.Panel value="rounds" pt="sm">
        <RoundsMatrix data={data} />
      </Tabs.Panel>

      <Tabs.Panel value="config" pt="sm">
        <ConfigPanel
          gameUuid={gameUuid}
          stations={stations}
          intervalSeconds={state.data?.settings.round_interval_seconds ?? 600}
          notifications={state.data?.settings.notifications_enabled ?? true}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['walkieState', gameUuid] });
            qc.invalidateQueries({ queryKey: ['walkieRounds', gameUuid] });
          }}
        />
      </Tabs.Panel>
    </Tabs>
  );
}

/* ---------------------------------------------------------------- stanoviště */

/** Držitele posílá server jen jako id skupiny; jméno a barvu bereme z breakdownu. */
function holderOf(station: WalkieStation): { name: string; color: string } | null {
  if (station.last_holder_group_id == null) return null;
  const hit = station.breakdown.find((b) => b.group_id === station.last_holder_group_id);
  return hit ? { name: hit.group_name, color: hit.group_color } : null;
}

function toMapStations(stations: WalkieStation[]): MapStation[] {
  return stations.map((s) => {
    const holder = holderOf(s);
    return {
      id: s.id,
      position: s.position,
      label: stationLabel(s),
      points: s.points,
      holderName: holder?.name ?? null,
      holderColor: holder?.color ?? null,
    };
  });
}

function StationsPanel({
  stations,
  rounds,
}: {
  gameUuid: string;
  stations: WalkieStation[];
  rounds: Awaited<ReturnType<typeof gamesApi.getWalkieRounds>>;
}) {
  const [detailId, setDetailId] = useState<number | null>(null);
  const detail = stations.find((s) => s.id === detailId) ?? null;

  if (stations.length === 0) {
    return <Card><Text c="dimmed" ta="center" py="lg">Hra zatím nemá nastavená stanoviště.</Text></Card>;
  }

  return (
    <Stack gap="sm">
      <Card padding={0} style={{ overflow: 'hidden' }}>
        <SituationMap stations={toMapStations(stations)} onStationClick={(s) => setDetailId(s.id)} />
      </Card>

      <Stack gap="sm">
        {stations.map((s) => {
          const holder = holderOf(s);
          const total = s.breakdown.reduce((a, b) => a + b.points, 0) || 1;
          return (
            <Card key={s.id} padding={0}>
              <Group p="sm" gap={8} style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
                <Badge variant="light" color="gray" style={numStyle}>{s.position}</Badge>
                <Text fw={700} fz="sm" style={{ flex: 1, minWidth: 0 }} truncate>{stationLabel(s)}</Text>
                <Badge variant="light" color="gray">{formatPoints(s.points)} b</Badge>
              </Group>
              <Stack p="sm" gap={8}>
                <Group gap={8}>
                  <Eyebrow>Drží</Eyebrow>
                  {holder
                    ? <GroupTag name={holder.name} color={holder.color} />
                    : <Text c="dimmed" fz="sm">
                        {s.has_last_call ? 'nikdo se neozval' : 'zatím bez zápisu'}
                      </Text>}
                  {s.none_count > 0 && (
                    <Eyebrow>{s.none_count}× se neozval nikdo</Eyebrow>
                  )}
                </Group>
                {s.breakdown.length > 0 && (
                  <Box>
                    <Eyebrow>Nabráno po skupinách</Eyebrow>
                    <Group gap={0} mt={5} style={{ height: 7, borderRadius: 999, overflow: 'hidden' }}>
                      {s.breakdown.map((b) => (
                        <Tooltip key={b.group_id} label={`${b.group_name}: ${formatPoints(b.points)} b`}>
                          <Box style={{ width: `${(b.points / total) * 100}%`, height: '100%', background: b.group_color }} />
                        </Tooltip>
                      ))}
                    </Group>
                    <Group gap={9} mt={6}>
                      {s.breakdown.map((b) => (
                        <Group key={b.group_id} gap={5} wrap="nowrap">
                          <span style={{ width: 8, height: 8, borderRadius: 2, background: b.group_color }} />
                          <Text fz="xs" style={numStyle}>{formatPoints(b.points)}</Text>
                        </Group>
                      ))}
                    </Group>
                  </Box>
                )}
              </Stack>
            </Card>
          );
        })}
      </Stack>

      <Modal opened={!!detail} onClose={() => setDetailId(null)} title={detail ? stationLabel(detail) : ''}>
        {detail && (
          <Stack gap="sm">
            <Group>
              <Eyebrow>Bodová hodnota</Eyebrow>
              <Text fw={600} style={numStyle}>{formatPoints(detail.points)} b</Text>
            </Group>
            <Group>
              <Eyebrow>Drží</Eyebrow>
              {holderOf(detail)
                ? <GroupTag name={holderOf(detail)!.name} color={holderOf(detail)!.color} />
                : <Text c="dimmed" fz="sm">nikdo se neozval</Text>}
            </Group>
            <Box>
              <Eyebrow>Historie kol</Eyebrow>
              <Stack gap={0} mt={6} style={{ border: '1px solid var(--mantine-color-default-border)', borderRadius: 5 }}>
                {[...rounds.rounds].reverse().map((r) => {
                  const call = r.calls.find((c) => c.station_id === detail.id);
                  return (
                    <Group key={r.round_number} p={8} gap={8} wrap="nowrap"
                      style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
                      <Text fz="xs" c="dimmed" style={numStyle} w={54}>kolo {r.round_number}</Text>
                      {!call
                        ? <Text fz="sm" c="dimmed">bez zápisu</Text>
                        : call.group_id == null
                          ? <Text fz="sm" c="dimmed">nikdo se neozval</Text>
                          : <><GroupTag name={call.group_name} color={call.group_color} />
                             <Text fz="xs" c="dimmed" style={numStyle}>+{formatPoints(call.points)}</Text></>}
                    </Group>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}

/* ------------------------------------------------------------- historie kol */

function RoundsMatrix({ data }: { data: Awaited<ReturnType<typeof gamesApi.getWalkieRounds>> }) {
  if (data.rounds.length === 0) {
    return <Card><Text c="dimmed" ta="center" py="lg">Zatím neproběhlo žádné kolo.</Text></Card>;
  }
  const lastRound = data.rounds[data.rounds.length - 1]?.round_number;

  return (
    <Card padding={0}>
      <Table.ScrollContainer minWidth={520}>
        <Table striped withColumnBorders={false} fz="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ position: 'sticky', left: 0, background: 'var(--mantine-color-body)', zIndex: 2 }}>
                Kolo
              </Table.Th>
              {data.stations.map((s) => (
                <Table.Th key={s.id} style={{ whiteSpace: 'nowrap' }}>
                  {stationLabel(s)}<br />
                  <Text component="span" c="dimmed" fz={10} style={numStyle}>{formatPoints(s.points)} b</Text>
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {[...data.rounds].reverse().map((r) => (
              <Table.Tr key={r.round_number}>
                <Table.Td style={{ position: 'sticky', left: 0, background: 'var(--mantine-color-body)', zIndex: 1, whiteSpace: 'nowrap' }}>
                  <Text component="span" fw={700} style={numStyle}>{r.round_number}</Text>{' '}
                  <Text component="span" c="dimmed" fz={10} style={numStyle}>
                    {new Date(r.started_at).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}
                  </Text>
                </Table.Td>
                {data.stations.map((s) => {
                  const call = r.calls.find((c) => c.station_id === s.id);
                  if (!call) {
                    // Kolo doběhlo bez zápisu, nebo stanoviště přibylo později.
                    return (
                      <Table.Td key={s.id}>
                        <Text c="dimmed" fz={10} style={numStyle}>
                          {r.round_number === lastRound && !r.completed_at ? 'čeká' : '—'}
                        </Text>
                      </Table.Td>
                    );
                  }
                  if (call.group_id == null) {
                    return <Table.Td key={s.id}><Text c="dimmed" fz={10} style={numStyle}>nikdo</Text></Table.Td>;
                  }
                  return (
                    <Table.Td key={s.id}>
                      <Tooltip label={`zapsáno za ${formatPoints(call.points)} b`}>
                        <Box><GroupTag name={call.group_name} color={call.group_color} /></Box>
                      </Tooltip>
                    </Table.Td>
                  );
                })}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Card>
  );
}

/* ---------------------------------------------------------------- nastavení */

function ConfigPanel({
  gameUuid,
  stations,
  intervalSeconds,
  notifications: notifyEnabled,
  onSaved,
}: {
  gameUuid: string;
  stations: WalkieStation[];
  intervalSeconds: number;
  notifications: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState<{ index: number; name: string; points: number | string } | null>(null);

  const save = useMutation({
    mutationFn: (payload: {
      stations: Array<{ position: number; name: string | null; points: number }>;
      roundIntervalSeconds?: number;
      notificationsEnabled?: boolean;
    }) => walkieApi.saveConfig(gameUuid, payload),
    onSuccess: () => {
      notifications.show({ message: 'Nastavení uloženo', color: 'forest' });
      setEditing(null);
      onSaved();
    },
    onError: (e: any) =>
      notifications.show({
        title: 'Uložení se nepovedlo',
        message: e?.response?.data?.error || e.message,
        color: 'red',
      }),
  });

  const asPayload = (list: WalkieStation[]) =>
    list.map((s, i) => ({ position: i + 1, name: s.name, points: s.points }));

  const currentMinutes = Math.round((intervalSeconds / 60) * 100) / 100;

  const setInterval = (minutes: number) =>
    save.mutate({ stations: asPayload(stations), roundIntervalSeconds: Math.round(minutes * 60) });

  const addStation = () => {
    if (stations.length >= MAX_STATIONS) {
      notifications.show({ message: `Víc než ${MAX_STATIONS} stanovišť server nepovolí.`, color: 'orange' });
      return;
    }
    save.mutate({
      stations: [...asPayload(stations), { position: stations.length + 1, name: null, points: 1 }],
    });
  };

  const removeStation = (index: number) => {
    if (stations.length <= 1) {
      notifications.show({ message: 'Aspoň jedno stanoviště musí zůstat.', color: 'orange' });
      return;
    }
    const next = stations.filter((_, i) => i !== index);
    save.mutate({ stations: asPayload(next) });
  };

  const submitEdit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!editing) return;
    const points = Number(editing.points);
    if (!Number.isFinite(points) || points < 0) {
      notifications.show({ message: 'Body musí být nezáporné číslo.', color: 'orange' });
      return;
    }
    const next = asPayload(stations);
    next[editing.index] = {
      ...next[editing.index],
      name: editing.name.trim() || null,
      points,
    };
    save.mutate({ stations: next });
  };

  return (
    <Stack gap="md">
      <Text c="dimmed" fz="sm">
        Stanoviště nejsou vázaná na zařízení — obvolává je člověk vysílačkou a zapisuje, kdo se ozval.
        Konfigurace platí pro hru na serveru, takže ji vidí web i všechna připojená zařízení.
      </Text>

      <Box>
        <Eyebrow>Čas do dalšího kola</Eyebrow>
        <ScrollArea type="never" mt={6}>
          <Chip.Group
            multiple={false}
            value={String(currentMinutes)}
            onChange={(v) => setInterval(Number(v))}
          >
            <Group gap={5} wrap="nowrap">
              {INTERVAL_PRESETS.map((m) => (
                <Chip key={m} value={String(m)} size="xs" disabled={save.isPending}>{m} min</Chip>
              ))}
              {!INTERVAL_PRESETS.includes(currentMinutes) && (
                <Chip value={String(currentMinutes)} size="xs">{currentMinutes} min</Chip>
              )}
            </Group>
          </Chip.Group>
        </ScrollArea>
      </Box>

      <Group wrap="nowrap" gap="md">
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text fw={650} fz="sm">Upozornit na další kolo</Text>
          <Text c="dimmed" fz="sm">Zařízení dostane lokální notifikaci, až vyprší interval.</Text>
        </Box>
        <Switch
          checked={notifyEnabled}
          disabled={save.isPending}
          aria-label="Upozornit na další kolo"
          onChange={() =>
            save.mutate({ stations: asPayload(stations), notificationsEnabled: !notifyEnabled })
          }
        />
      </Group>

      <Box>
        <Group justify="space-between" mb={6}>
          <Eyebrow>Stanoviště · {stations.length}</Eyebrow>
          <Button size="xs" variant="default" leftSection={<Plus size={13} />}
            onClick={addStation} loading={save.isPending}>
            Přidat stanoviště
          </Button>
        </Group>
        <Card padding={0}>
          <Table.ScrollContainer minWidth={360}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={44}>#</Table.Th>
                  <Table.Th>Název</Table.Th>
                  <Table.Th w={104} ta="right">Body</Table.Th>
                  <Table.Th w={80} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {stations.map((s, i) => (
                  <Table.Tr key={s.id}>
                    <Table.Td><Badge variant="light" color="gray" style={numStyle}>{s.position}</Badge></Table.Td>
                    <Table.Td fw={600} fz="sm">{stationLabel(s)}</Table.Td>
                    <Table.Td ta="right"><Text fz="sm" style={numStyle}>{formatPoints(s.points)} b</Text></Table.Td>
                    <Table.Td>
                      <Group gap={2} justify="flex-end" wrap="nowrap">
                        <ActionIcon aria-label={`Upravit ${stationLabel(s)}`}
                          onClick={() => setEditing({ index: i, name: s.name ?? '', points: s.points })}>
                          <Pencil size={14} />
                        </ActionIcon>
                        <ActionIcon color="red" aria-label={`Smazat ${stationLabel(s)}`}
                          onClick={() => {
                            if (window.confirm(`Smazat ${stationLabel(s)}? Zápisy z předchozích kol zůstanou.`)) {
                              removeStation(i);
                            }
                          }}>
                          <Trash2 size={14} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
        <Text c="dimmed" fz="xs" mt={6}>
          Změna bodové hodnoty platí pro nová kola. Body zapsané v předchozích kolech zůstávají,
          protože se u každého zápisu ukládá hodnota platná v tu chvíli.
        </Text>
      </Box>

      <Modal opened={!!editing} onClose={() => setEditing(null)} title="Stanoviště">
        {editing && (
          <form onSubmit={submitEdit}>
            <Stack gap="sm">
              <TextInput
                label="Název"
                placeholder={`Stanoviště ${editing.index + 1}`}
                value={editing.name}
                maxLength={60}
                onChange={(e) => setEditing({ ...editing, name: e.currentTarget.value })}
                data-autofocus
              />
              <NumberInput
                label="Bodová hodnota"
                description="Desetinná hodnota je v pořádku, např. 3,5."
                value={editing.points}
                onChange={(v) => setEditing({ ...editing, points: v })}
                min={0}
                step={0.5}
                decimalScale={2}
              />
              <Group justify="flex-end" mt="xs">
                <Button variant="default" type="button" onClick={() => setEditing(null)}>Zrušit</Button>
                <Button type="submit" leftSection={<Check size={14} />} loading={save.isPending}>Uložit</Button>
              </Group>
            </Stack>
          </form>
        )}
      </Modal>
    </Stack>
  );
}
